import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { UsersService } from './users.service';
import { AuthenticationService } from './auth.service';
import { EventService } from './event.service';
import { EventType } from '../constants/events';
import { Provider } from '../models/users.model';

@Injectable({
  providedIn: 'root',
})
export class UserConfigPollingService {
  private pollingInterval: any = null;
  private readonly POLLING_INTERVAL = 60000;
  private isPollingActive = false;
  private capabilitiesChangedSubject = new Subject<any>();
  private tenantsChangedSubject = new Subject<any>();

  constructor(
    private usersService: UsersService,
    private authService: AuthenticationService,
    private eventService: EventService
  ) {}

  /**
   * Iniciar el polling de configuración de usuario
   */
  startPolling(): void {
    if (this.isPollingActive) {
      return;
    }

    this.isPollingActive = true;
    this.setupVisibilityListener();
    this.performCheck();

    this.pollingInterval = setInterval(() => {
      if (!document.hidden && this.isPollingActive) {
        this.performCheck();
      }
    }, this.POLLING_INTERVAL);
  }

  /**
   * Detener el polling de configuración de usuario
   */
  stopPolling(): void {
    this.isPollingActive = false;
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Obtener observable de cambios de capacidades
   */
  onCapabilitiesChanged(): Observable<any> {
    return this.capabilitiesChangedSubject.asObservable();
  }

  /**
   * Obtener observable de cambios de tenants
   */
  onTenantsChanged(): Observable<any> {
    return this.tenantsChangedSubject.asObservable();
  }

  /**
   * Realizar verificación de configuración de usuario
   */
  private performCheck(): void {
    const currentUser = this.authService.currentUser();
    const token = this.authService.getToken();

    if (!currentUser || !token || !this.isPollingActive) {
      return;
    }

    this.usersService.getUserData(token).subscribe(
      (response: any) => {
        if (response.data && this.isPollingActive) {
          const capabilitiesChanged = this.checkCapabilitiesChanged(
            response.data.capabilities || [],
            currentUser.capabilities || []
          );

          const tenantsChanged =
            (currentUser.isAdmin || response.data.isAdmin) &&
            this.checkTenantsChanged(
              response.data.providers || [],
              currentUser.providers || []
            );

          if (capabilitiesChanged) {
            const userData = {
              id: response.data.id,
              email: response.data.email,
              active: response.data.active,
              name: response.data.name,
              surname: response.data.surname,
              phone: response.data.phone,
              capabilities: response.data.capabilities,
              token: token,
              notificationPush: response.data.notificationPush,
              notificationEmail: response.data.notificationEmail,
              providers: response.data.providers,
              isAdmin: response.data.isAdmin,
            };
            sessionStorage.setItem('cayc-user', JSON.stringify(userData));
            this.authService.clearUserCache();

            this.stopPolling();
            this.capabilitiesChangedSubject.next({
              oldCapabilities: currentUser.capabilities,
              newCapabilities: response.data.capabilities,
            });
            this.eventService.broadcast(
              EventType.USER_CAPABILITIES_UPDATED,
              true
            );
          } else if (tenantsChanged) {
            const userData = {
              id: response.data.id,
              email: response.data.email,
              active: response.data.active,
              name: response.data.name,
              surname: response.data.surname,
              phone: response.data.phone,
              capabilities: response.data.capabilities,
              token: token,
              notificationPush: response.data.notificationPush,
              notificationEmail: response.data.notificationEmail,
              providers: response.data.providers,
              isAdmin: response.data.isAdmin,
            };
            sessionStorage.setItem('cayc-user', JSON.stringify(userData));
            this.authService.clearUserCache();

            this.stopPolling();
            this.tenantsChangedSubject.next({
              oldProviders: currentUser.providers,
              newProviders: response.data.providers,
            });
          }
        }
      },
      (error: any) => {
        console.warn('Error en polling de configuración de usuario:', error);
      }
    );
  }

  /**
   * Verificar si las capacidades han cambiado
   */
  private checkCapabilitiesChanged(
    serverCapabilities: any[],
    currentCapabilities: any[]
  ): boolean {
    const normalizeCapabilities = (caps: any[]) => {
      return caps
        .map((cap: any) => {
          if (typeof cap === 'object' && cap.key) {
            return cap.key;
          }
          return cap;
        })
        .sort();
    };

    const serverCapsNormalized = normalizeCapabilities(serverCapabilities);
    const currentCapsNormalized = normalizeCapabilities(currentCapabilities);

    return (
      JSON.stringify(serverCapsNormalized) !==
      JSON.stringify(currentCapsNormalized)
    );
  }

  /**
   * Verificar si hay cambios en tenants/providers
   */
  private checkTenantsChanged(
    serverProviders: Provider[],
    currentProviders: Provider[]
  ): boolean {
    if (!currentProviders || currentProviders.length === 0) {
      return serverProviders && serverProviders.length > 0;
    }

    if (!serverProviders || serverProviders.length === 0) {
      return currentProviders.length > 0;
    }

    if (serverProviders.length !== currentProviders.length) {
      return true;
    }

    const currentProviderIds = new Set(currentProviders.map((p) => p.id));
    const serverProviderIds = new Set(serverProviders.map((p) => p.id));

    const hasNewProvider = Array.from(serverProviderIds).some(
      (id) => !currentProviderIds.has(id)
    );

    const hasRemovedProvider = Array.from(currentProviderIds).some(
      (id) => !serverProviderIds.has(id)
    );

    return hasNewProvider || hasRemovedProvider;
  }

  /**
   * Configurar listener de visibilidad de pestaña
   */
  private setupVisibilityListener(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // Pestaña en background - no hacer nada, el intervalo ya verifica document.hidden
      } else {
        if (this.isPollingActive) {
          this.performCheck();
        }
      }
    });
  }
}
